import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Subscription } from "rxjs";
import { CaseService } from "../../services/case.service";
import { SignalingService } from "../../services/signaling.service";
import * as uuid from 'uuid';
import { CameraStreamOrientation, getCameraStreamOrientation } from '../../utils/camera-stream-orientation';

@Component({
    selector: 'app-camera-device',
    templateUrl: './camera-device.component.html',
    styleUrls: ['./camera-device.component.scss']
})
export class CameraDeviceComponent implements OnInit, OnDestroy {

    private subscriptions: Subscription[] = [];
    private attachRetryCount: Map<string, number> = new Map<string, number>();
    private attachRetryTimers: any[] = [];
    private remoteVideoTrack: any | null = null;
    private currentOrientation?: CameraStreamOrientation;
    private hasReportedOrientation = false;
    public uniqueId: string = 'cam-' + uuid.v4();

    public remoteAudioTrack: any | null = null;
    public showBlackScreen: boolean = true;
    public isAudioMuted: boolean = true;

    @Input('id') cameraId: string = '';
    // @Input('investigatorId') investigatorId: string = '';
    @Input('name') name: string = '';
    @Input() isSingle: boolean = false;
    @Input() showLocateButton: boolean = false;
    @Input() showStopButton: boolean = false;
    /** Building id for indoor-alerts topic (tablet camera off signal). */
    @Input() buildingId: string = '';
    /** Floor id for indoor-alerts topic (tablet camera off signal). */
    @Input() floorId: string = '';
    /** Room name for indoor-alerts topic (tablet camera off signal). */
    @Input() roomName: string = '';
    /** Tablet device id sent in `{ isEnabled, id }` — usually `camera_id` for tablet-cams. */
    @Input() tabletId: string = '';
    @Output() locate = new EventEmitter<void>();
    @Output() stopStream = new EventEmitter<void>();
    @Output() orientationChange = new EventEmitter<CameraStreamOrientation | undefined>();

    get remoteVideo(): HTMLVideoElement | null {
        return document.getElementById('video-' + this.uniqueId) as HTMLVideoElement;
    }

    get remoteAudio(): HTMLAudioElement | null {
        return document.getElementById('audio-' + this.uniqueId) as HTMLAudioElement;
    }

    constructor(
        private caseService: CaseService,
        private signalingService: SignalingService
    ) { }

    get hasAudioTrackAvailable(): boolean {
        if (this.remoteAudioTrack) {
            return true;
        }
        const tracks = this.caseService.getCameraTracks(this.cameraId) || [];
        return tracks.some((track: any) => track?.getType && track.getType() === 'audio');
    }

    /** Detach from this tile without dropping the shared room, so another page can show it. */
    releasePlayback(): void {
        const videoTrack = this.remoteVideoTrack;
        const audioTrack = this.remoteAudioTrack;
        this.remoteVideoTrack = null;
        this.remoteAudioTrack = null;
        try {
            videoTrack?.detach(this.remoteVideo);
        } catch { }
        try {
            audioTrack?.detach(this.remoteAudio);
        } catch { }
        this.showBlackScreen = true;
    }

    /** Reattach live tracks after the tile was taken off screen and shown again. */
    refreshPlayback(): void {
        this.remoteVideoTrack = null;
        this.remoteAudioTrack = null;
        this.restoreTracksFromState();
    }

    async ngOnInit(): Promise<void> {
        this.reportVideoOrientation(undefined);
        this.isAudioMuted = this.caseService.getCameraAudioMuted(this.cameraId);
        this.subscriptions.push(
            this.caseService.cameraStreamChanged.subscribe((payload: any) => {
                if (!payload) return;
                const { investigatorId, track, state } = payload;
                if (investigatorId !== this.cameraId) return;
                if (state === 'started' && track) {
                    if (track === this.remoteVideoTrack || track === this.remoteAudioTrack) {
                        return;
                    }
                    this.onTrackAdded(track);
                } else if (state === 'destroyed') {
                    if (track) {
                        this.onTrackRemoved(track);
                    } else {
                        this.cleanupTracks();
                        this.showBlackScreen = true;
                    }
                } else if (payload.visible === false && !track) {
                    this.cleanupTracks();
                    this.showBlackScreen = true;
                }
            })
        );

        // Component can mount after track events were already emitted.
        this.restoreTracksFromState();
    }

    ngOnDestroy(): void {
        this.attachRetryTimers.forEach((timer) => clearTimeout(timer));
        this.attachRetryTimers = [];
        this.subscriptions.forEach(el => el.unsubscribe());
        this.cleanupTracks();
    }

    private onTrackAdded(track: any) {
        // console.log('[CameraDevice] TRACK_ADDED', {
        //     isLocal: track.isLocal && track.isLocal(),
        //     type: track.getType && track.getType(),
        //     participantId: track.getParticipantId && track.getParticipantId()
        // });

        if (!track) {
            return;
        }
        if (track.isLocal && track.isLocal()) {
            // console.log('[CameraDevice] Ignoring local track');
            return;
        }

        const trackType = track.getType && track.getType();
        if (trackType === 'audio') {
            this.attachAudioTrack(track);
            return;
        }

        if (trackType !== 'video') {
            // console.log('[CameraDevice] Ignoring non-video track');
            return;
        }

        this.attachVideoTrack(track);
    }

    private attachVideoTrack(track: any) {
        this.showBlackScreen = true;
        this.reportVideoOrientation(undefined);

        // Always show only one remote participant's video
        if (this.remoteVideoTrack) {
            // console.log('[CameraDevice] Detaching previous remote video track');
            this.remoteVideoTrack.detach(this.remoteVideo);
        }

        this.remoteVideoTrack = track;

        const videoNode = this.remoteVideo;
        if (!videoNode) {
            console.warn('[CameraDevice] remoteVideo element not found');
            this.scheduleTrackAttachRetry(track, 'video');
            return;
        }
        this.clearTrackAttachRetry(track, 'video');

        videoNode.autoplay = true;
        videoNode.muted = true; // allow autoplay without user gesture
        (videoNode as any).playsInline = true;
        videoNode.style.width = '100%';
        videoNode.style.height = '100%';
        videoNode.style.objectFit = 'contain';

        // const originalStream = track.getOriginalStream && track.getOriginalStream();
        // if (originalStream) {
        //     const videoTracks = originalStream.getVideoTracks ? originalStream.getVideoTracks() : [];
        //     console.log('[CameraDevice] originalStream videoTracks', videoTracks.map(t => ({
        //         readyState: t.readyState,
        //         muted: t.muted,
        //         enabled: t.enabled
        //     })));
        //     // videoNode.srcObject = originalStream;
        //     if (!videoTracks.length) {
        //         this.showBlackScreen = true;
        //     }
        // }

        videoNode.addEventListener('loadedmetadata', () => {
            // console.log('[CameraDevice] video loadedmetadata', {
            //     videoWidth: videoNode.videoWidth,
            //     videoHeight: videoNode.videoHeight
            // });
        });
        videoNode.addEventListener('playing', () => {
            // console.log('[CameraDevice] video playing', {
            //     readyState: videoNode.readyState,
            //     currentTime: videoNode.currentTime
            // });
            this.showBlackScreen = false;
        });
        videoNode.addEventListener('error', (e) => {
            console.warn('[CameraDevice] video error', e);
        });

        // console.log('[CameraDevice] Attaching track', {
        //     trackId: track.getId && track.getId(),
        //     muted: track.isMuted && track.isMuted(),
        //     videoType: track.getVideoType && track.getVideoType()
        // });
        track.attach(videoNode);

        const playPromise = videoNode.play();
        if (playPromise && typeof playPromise.then === 'function') {
            playPromise
                .then(() => {
                    // console.log('[CameraDevice] videoNode.play resolved', videoNode.readyState);
                })
                .catch((err: any) => {
                    console.warn('[CameraDevice] videoNode.play rejected', err);
                });
        }
    }

    onVideoDimensionsChange(event: Event): void {
        const video = event.currentTarget as HTMLVideoElement | null;
        const orientation = video
            ? getCameraStreamOrientation(video.videoWidth, video.videoHeight)
            : undefined;

        this.reportVideoOrientation(orientation);
    }

    private reportVideoOrientation(orientation: CameraStreamOrientation | undefined): void {
        if (this.hasReportedOrientation && orientation === this.currentOrientation) {
            return;
        }

        this.hasReportedOrientation = true;
        this.currentOrientation = orientation;
        this.orientationChange.emit(orientation);
    }

    private attachAudioTrack(track: any) {
        // console.log('[CameraDevice] AUDIO_TRACK_ADDED', {
        //     isLocal: track.isLocal && track.isLocal(),
        //     trackId: track.getId && track.getId()
        // });

        if (this.remoteAudioTrack) {
            // console.log('[CameraDevice] Detaching previous audio track');
            this.remoteAudioTrack.detach(this.remoteAudio);
        }

        this.remoteAudioTrack = track;
        const audioNode = this.remoteAudio;
        if (!audioNode) {
            console.warn('[CameraDevice] remoteAudio element not found');
            this.scheduleTrackAttachRetry(track, 'audio');
            return;
        }
        this.clearTrackAttachRetry(track, 'audio');

        audioNode.autoplay = true;
        this.applyAudioMuteToElement();

        track.attach(audioNode);
        this.playAttachedAudio(audioNode);
    }

    private playAttachedAudio(audioNode: HTMLAudioElement): void {
        const playNode = () => {
            const playPromise = audioNode.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise.catch((err: any) => {
                    console.error('[CameraDevice] audioNode.play initial failed', err.name, err.message);
                });
            }
        };

        if (this.isAudioMuted) {
            playNode();
            return;
        }

        this.caseService.startCameraAudioPlayback()
            .then(playNode)
            .catch(playNode);
    }

    private onTrackRemoved(track: any) {
        // console.log('[CameraDevice] TRACK_REMOVED', {
        //     isLocal: track.isLocal && track.isLocal(),
        //     type: track.getType && track.getType()
        // });

        if (this.remoteVideoTrack === track) {
            track.detach(this.remoteVideo);
            this.remoteVideoTrack = null;
            this.showBlackScreen = true;
            this.reportVideoOrientation(undefined);
            this.clearTrackAttachRetry(track, 'video');
        }
        if (this.remoteAudioTrack === track) {
            track.detach(this.remoteAudio);
            this.remoteAudioTrack = null;
            this.clearTrackAttachRetry(track, 'audio');
        }
    }

    private cleanupTracks() {
        try {
            if (this.remoteVideoTrack && this.remoteVideo) {
                try {
                    this.remoteVideoTrack.detach(this.remoteVideo);
                } catch { }
            }
            this.remoteVideoTrack = null;
            this.reportVideoOrientation(undefined);
            if (this.remoteAudioTrack && this.remoteAudio) {
                try {
                    this.remoteAudioTrack.detach(this.remoteAudio);
                } catch { }
            }
            this.remoteAudioTrack = null;
        } catch (e) {
            console.error('CameraDevice cleanup error', e);
        }
    }

    private getTrackAttachRetryKey(track: any, kind: 'audio' | 'video'): string {
        const id = track?.getId ? track.getId() : track?.sid || `${this.cameraId}-${kind}`;
        return `${kind}:${id}`;
    }

    private clearTrackAttachRetry(track: any, kind: 'audio' | 'video'): void {
        const key = this.getTrackAttachRetryKey(track, kind);
        this.attachRetryCount.delete(key);
    }

    private scheduleTrackAttachRetry(track: any, kind: 'audio' | 'video'): void {
        const key = this.getTrackAttachRetryKey(track, kind);
        const nextTry = (this.attachRetryCount.get(key) || 0) + 1;
        if (nextTry > 12) {
            console.warn('[CameraDevice] attach retry limit reached', { kind, key });
            return;
        }
        this.attachRetryCount.set(key, nextTry);

        const timer = setTimeout(() => {
            if (kind === 'audio') {
                this.attachAudioTrack(track);
            } else {
                this.attachVideoTrack(track);
            }
        }, 200);
        this.attachRetryTimers.push(timer);
    }

    private restoreTracksFromState(): void {
        const tracks = this.caseService.getCameraTracks(this.cameraId) || [];
        if (!tracks.length) return;

        tracks
            .slice()
            .sort((a: any, b: any) => {
                const aType = a?.getType ? a.getType() : '';
                const bType = b?.getType ? b.getType() : '';
                if (aType === bType) return 0;
                return aType === 'video' ? -1 : 1;
            })
            .forEach((track: any) => this.onTrackAdded(track));
    }

    private attachAudioTrackFromState(): boolean {
        const tracks = this.caseService.getCameraTracks(this.cameraId) || [];
        const audioTrack = tracks.find((track: any) => track?.getType && track.getType() === 'audio');
        if (!audioTrack) return false;
        this.attachAudioTrack(audioTrack);
        return true;
    }

    onLocateClick(event?: Event): void {
        event?.stopPropagation();
        event?.preventDefault();
        this.locate.emit();
    }

    onStopStreamClick(event?: Event): void {
        event?.stopPropagation();
        event?.preventDefault();
        this.publishTabletCameraDisabledSignal();
        this.stopStream.emit();
    }

    private publishTabletCameraDisabledSignal(): void {
        const buildingId = String(this.buildingId || '').trim();
        const floorId = String(this.floorId || '').trim();
        const roomName = String(this.roomName || '').trim();
        const tabletId = String(this.tabletId || '').trim();
        const topicSuffix = !roomName
            ? 'cameras'
            : `${floorId}-${roomName.replaceAll(' ', '_')}`;
        if (!buildingId) {
            return;
        }
        this.signalingService.publish('', {
            isEnabled: false,
            id: tabletId
        }, `${this.signalingService.indoorAlertsTopicName}-${buildingId}-${topicSuffix}`);
    }

    async toggleAudio($event: any) {
        $event.preventDefault();
        $event.stopPropagation();

        await this.caseService.startCameraAudioPlayback();

        if (!this.remoteAudioTrack) {
            this.attachAudioTrackFromState();
        }
        if (!this.remoteAudioTrack) {
            return;
        }

        this.isAudioMuted = !this.isAudioMuted;
        this.caseService.setCameraAudioMuted(this.cameraId, this.isAudioMuted);
        this.applyAudioMuteToElement();
        if (this.remoteAudio && !this.isAudioMuted) {
            await this.caseService.startCameraAudioPlayback();
            const playPromise = this.remoteAudio.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise.catch((err: any) => {
                    console.error('[CameraDevice] audioNode.play on unmute failed', err?.name, err?.message);
                });
            }
        }
    }

    private applyAudioMuteToElement(): void {
        if (!this.remoteAudio) {
            return;
        }
        this.remoteAudio.muted = this.isAudioMuted;
        this.remoteAudio.volume = this.isAudioMuted ? 0 : 1.0;
    }
}
